import React from "react";
import {Box, Button, Paper, Stack, Typography, useTheme} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {Division} from "../../data/Division";
import {LimitedResourceResponse} from "../../data/LimitedResourceResponse";
import {UserContextProps} from "../../hooks/useUser";
import {LoadingContent} from "../LoadingContent";
import {ResourceCard} from "./ResourceCard";
import {ResourceFilterChip} from "./ResourceFilterChip";
import {themedScrollbar} from "./themedScrollbar";

interface ResourceContentPanelProps {
    division: Division | null;
    divisionIdentifier: string | null;
    divisions: Division[];
    resources: LimitedResourceResponse[];
    isLoading: boolean;
    typeFilter: string;
    onTypeFilterChange: (filter: string) => void;
    searchQuery: string;
    user: UserContextProps;
    isGlobalSearch: boolean;
}

export function ResourceContentPanel({
    division,
    divisionIdentifier,
    divisions,
    resources,
    isLoading,
    typeFilter,
    onTypeFilterChange,
    searchQuery,
    user,
    isGlobalSearch,
}: ResourceContentPanelProps) {
    const theme = useTheme();

    // Build a lookup map for division display names
    const divisionNameMap = React.useMemo(() => {
        const map: Record<string, string> = {};
        divisions.forEach((d) => { map[d.identifier] = d.displayName; });
        return map;
    }, [divisions]);

    return (
        <Box sx={{flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column"}}>
            {/* Division header — only show when a specific division is selected */}
            {division && (
                <Paper variant="outlined" sx={{p: 2.5, mb: 2}}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                            component="img"
                            src={division.badge}
                            loading="lazy"
                            decoding="async"
                            width={56}
                            height={56}
                            sx={{width: 56, height: 56, borderRadius: "8px"}}
                        />
                        <Box>
                            <Typography variant="h6" sx={{fontWeight: 600}}>
                                {division.displayName}
                            </Typography>
                            {division.description && (
                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                    {division.description}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            )}

            {/* Filter chips + new resource button */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, flexWrap: "wrap", gap: 1}}>
                <ResourceFilterChip
                    label="ALL"
                    active={typeFilter === "all"}
                    onClick={() => onTypeFilterChange("all")}
                />
                <ResourceFilterChip
                    label="DOCUMENT"
                    active={typeFilter === "content"}
                    onClick={() => onTypeFilterChange("content")}
                    color="#00bcd4"
                />
                <ResourceFilterChip
                    label="LINK"
                    active={typeFilter === "link"}
                    onClick={() => onTypeFilterChange("link")}
                    color="#7c4dff"
                />
                <Box sx={{flexGrow: 1}}/>
                {divisionIdentifier && user.hasPermissions(`${divisionIdentifier}-management:panel`) && (
                    <Button
                        component={RouterLink}
                        to={`/division-management/${divisionIdentifier.replaceAll("_", "-")}`}
                        variant="outlined"
                        size="small"
                    >
                        Manage Division
                    </Button>
                )}
                {divisionIdentifier && user.hasPermissions(`${divisionIdentifier}-resource:create`) && (
                    <Button
                        component={RouterLink}
                        to={`/resources/new/${divisionIdentifier.replaceAll("_", "-")}`}
                        variant="outlined"
                        color="success"
                        size="small"
                    >
                        New Resource
                    </Button>
                )}
            </Stack>

            {/* Resource list */}
            <Box sx={{
                flexGrow: 1,
                overflowY: "auto",
                ...themedScrollbar(theme),
            }}>
                <LoadingContent isLoading={isLoading}>
                    {resources.length === 0 ? (
                        <Paper variant="outlined" sx={{p: 4, textAlign: "center"}}>
                            <Typography color="text.secondary">
                                {searchQuery || typeFilter !== "all"
                                    ? "No resources match your filters."
                                    : "No resources available for this division."}
                            </Typography>
                        </Paper>
                    ) : (
                        resources.map((resource) => (
                            <ResourceCard
                                key={`${resource.divisionIdentifier ?? divisionIdentifier}-${resource.id}`}
                                resource={resource}
                                divisionIdentifier={resource.divisionIdentifier ?? divisionIdentifier ?? ""}
                                divisionDisplayName={divisionNameMap[resource.divisionIdentifier ?? divisionIdentifier ?? ""] ?? ""}
                                user={user}
                                showDivision={isGlobalSearch}
                            />
                        ))
                    )}
                </LoadingContent>
            </Box>
        </Box>
    );
}
